<script lang="ts">
  /* THE PLAN — what the route says, and the settings that change it.
   *
   * The body of what used to be the desktop `.routebox` and the phone route
   * sheet. `RouteContent.svelte` existed purely so those two could not drift;
   * with one panel chrome for both viewports there is nothing left for it to
   * hold apart, so its markup lives here and the file is gone.
   *
   * ═══ THE CONTROLS LIVE WITH THE ROUTE THEY CHANGE ═══
   *
   * A separate settings panel would mean ticking a box somewhere else and
   * coming back to see whether it did anything. Changing any of these re-solves
   * the same two points immediately, so the answer to "what does timing this to
   * slack cost me" is the panel redrawing.
   */
  import { autoroute, CHS_NOTICE } from '@gennetay/chart-core/lib/autoroute.svelte';
  import { editor } from '@gennetay/chart-core/lib/editor.svelte';
  import { routePlan } from '@gennetay/chart-core/lib/routeplan.svelte';
  import { fmtEte, fmtNm } from '@gennetay/chart-core/lib/nav';
  import { PLANNED_SPEED_KN, routeDistanceM, eteEtaFor } from '@gennetay/chart-core/lib/routesummary';
  import Button from '@gennetay/chart-ui/primitives/Button';
  import ButtonRow from '@gennetay/chart-ui/primitives/ButtonRow';
  import Notice from '@gennetay/chart-ui/primitives/Notice';
  import SectionHeader from '@gennetay/chart-ui/primitives/SectionHeader';
  import Stat from '@gennetay/chart-ui/primitives/Stat';
  import StatRow from '@gennetay/chart-ui/primitives/StatRow';
  import PassageControls from '@gennetay/chart-ui/PassageControls';
  import SchedulePanel from '@gennetay/chart-ui/SchedulePanel';
  import { resolveRoute } from '../route.svelte';
  import { waitingMs, underwayMs } from '@gennetay/chart-core/lib/passage';

  const distM = $derived(routeDistanceM(editor.pts));
  const ete = $derived(distM === null ? null : eteEtaFor(distM / 1852, null));
  const sched = $derived(autoroute.schedule);
</script>

{#if autoroute.result}
  <!-- ═══ THE ANSWER, AT THE TOP AND IN THE LARGEST TYPE ON THE PANEL ═══
       The hosted chart has never had a large numeral: "how long is this
       passage" rendered as 11 px mono in a definition list. A trip planner
       that will not say its answer out loud is not competing with anything. -->
  {#if sched}
    <StatRow>
      <Stat label="Under way" value={fmtEte(underwayMs(sched) / 1000)} size="lg" />
      {#if waitingMs(sched) > 0}
        <!-- NEVER folded into "under way". A route 40 minutes slower because it
             holds for slack is a different plan from one 40 minutes slower
             because the tide is foul, and the skipper decides which they
             wanted. -->
        <Stat label="Waiting" value={fmtEte(waitingMs(sched) / 1000)} size="lg" tone="warn" />
      {/if}
    </StatRow>
    <div class="gap"></div>
    <StatRow>
      <Stat label="Distance" value={distM === null ? '—' : fmtNm(distM)} unit="nm" />
      <Stat label="Depart" value={hhmm(sched.departT)} />
      <Stat label="Arrive" value={hhmm(sched.arriveT)} />
    </StatRow>
  {:else}
    <StatRow>
      <Stat label="Distance" value={distM === null ? '—' : fmtNm(distM)} unit="nm" size="lg" />
      <!-- The assumption behind the number, said out loud: a chart with no boat
           has no speed over ground, so the ETE is at a planned speed and the
           panel names it rather than implying one. -->
      <Stat label="ETE @ {PLANNED_SPEED_KN} kn" value={ete ? fmtEte(ete.eteS) : '—'} size="lg" />
    </StatRow>
  {/if}

  <SectionHeader title="Passage" />
  <PassageControls onchange={resolveRoute} />

  {#if sched}
    <SectionHeader title="Schedule" />
    <SchedulePanel schedule={sched} />
  {/if}

  <SectionHeader title="Edit" />
  <!-- How many waypoints the router made. Not a headline stat — nobody plans
       around it — but it is how you tell a route that bends round something
       from one that goes straight, and the old panel carried it. -->
  <p class="hint"><span class="pts">{editor.pts.length}</span> points. Tap a leg to add one, tap a
    point to select it, drag one to move it.</p>
  <!-- Beside the route they edit, not pinned under it. See TripFoot. -->
  <ButtonRow align="start">
    <Button
      intent="danger"
      disabled={editor.selected === null || editor.pts.length <= 2}
      onclick={() => editor.selected !== null && editor.remove(editor.selected)}
    >Delete point</Button>
    <Button intent="ghost" disabled={!editor.canUndo} onclick={() => editor.undo()}>Undo</Button>
    <!-- ═══ CLOSING A PANEL MUST NOT DESTROY THE PLAN ═══
         The old route box's ✕ called `routePlan.off()` — it looked like a
         dismiss and it was a delete, with no undo and no warning. The panel's ✕
         puts the panel away and leaves the route on the chart; throwing the
         route away is a named action, here, with the other things that change
         it. -->
    <Button intent="ghost" onclick={() => routePlan.off()}>Clear route</Button>
  </ButtonRow>

  {#each autoroute.warnings as w (w.code)}
    <Notice tone="warn">{w.text}</Notice>
  {/each}
{:else if autoroute.unsolvable}
  <SectionHeader title="No route" />
  <Notice tone="warn">{autoroute.unsolvable}</Notice>
{:else if autoroute.error}
  <SectionHeader title="Failed" />
  <Notice tone="bad">{autoroute.error}</Notice>
{/if}

<!-- Required, and subordinate. This is the ~430-character CHS advisory that
     travels inside every exported GPX; it is the same acknowledgement the
     credit line carries, and the reason a depth-only router is allowed to draw
     a line at all. It may not be shortened or hidden — but required is not the
     same as loud. -->
<Notice>{CHS_NOTICE}</Notice>

<script lang="ts" module>
  const hhmm = (t: number) =>
    new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
</script>

<style>
  .gap {
    height: var(--sp-5);
  }
  .pts {
    font-family: var(--mono);
    color: var(--ink);
    font-variant-numeric: tabular-nums;
  }
  .hint {
    margin: var(--sp-5) 0 0;
    font: var(--fs-read) / var(--lh-read) var(--sans);
    color: var(--sub);
  }
</style>
